/**
 * M27 Step 4 — Jobs & Grades & Key Positions (PR 5).
 */

import { useQuery } from "@tanstack/react-query";
import {
  BriefcaseBusiness,
  ChevronRight,
  GitBranch,
  Sparkles,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { AIHelpsHereCallout } from "../../components/onboarding/AIHelpsHereCallout";
import { api } from "../../lib/api";
import { useLegalEntityGate } from "../../lib/legalEntity";
import { StepLayout } from "./StepLayout";

export function Step4JobsGradesReal() {
  const { t } = useTranslation();
  const { legalEntityId: le, gate } = useLegalEntityGate();

  const grades = useQuery({
    queryKey: ["grades", le],
    queryFn: () => api.listGrades(le),
    enabled: !!le,
  });
  const jobs = useQuery({
    queryKey: ["jobs", le],
    queryFn: () => api.listJobs(le),
    enabled: !!le,
  });
  const positions = useQuery({
    queryKey: ["positions", le],
    queryFn: () => api.listPositions(le),
    enabled: !!le,
  });

  if (gate) return gate;

  const gradeCount = grades.data?.length ?? 0;
  const jobCount = jobs.data?.length ?? 0;
  const positionCount = positions.data?.length ?? 0;

  return (
    <StepLayout
      slug="jobs-grades"
      title={t("app.onb_step_jobs_grades")}
      subtitle={t("app.s4_subtitle")}
      continueLabel={t("app.s4_continue_to_employees")}
    >
      <div className="flex flex-col gap-5">
        <AIHelpsHereCallout
          message={t("app.s4_ai_msg")}
          detail={t("app.s4_ai_detail")}
        />

        <SectionCard
          icon={GitBranch}
          title={t("app.s4_grade_title")}
          count={gradeCount}
          countLabel={
            gradeCount === 1
              ? t("app.s4_grade_count_one")
              : t("app.s4_grade_count_many")
          }
          body={t("app.s4_grade_body")}
          ctaLabel={t("app.s4_grade_cta")}
          ctaTo="/policies/grades"
        />

        <SectionCard
          icon={BriefcaseBusiness}
          title={t("app.s4_jobs_title")}
          count={jobCount}
          countLabel={
            jobCount === 1 ? t("app.s4_jobs_count_one") : t("app.s4_jobs_count_many")
          }
          body={t("app.s4_jobs_body")}
          ctaLabel={t("app.s4_jobs_cta")}
          ctaTo="/people/jobs"
        />

        <SectionCard
          icon={Users}
          title={t("app.s4_positions_title")}
          count={positionCount}
          countLabel={
            positionCount === 1
              ? t("app.s4_positions_count_one")
              : t("app.s4_positions_count_many")
          }
          body={t("app.s4_positions_body")}
          ctaLabel={t("app.s4_positions_cta")}
          ctaTo="/people/positions"
        />

        <section className="rounded-xl border border-ai-subtle bg-ai-bg p-4">
          <header className="flex items-center gap-2">
            <Sparkles size={14} className="text-brand-teal" aria-hidden />
            <h2 className="text-sm font-semibold text-text-primary">
              {t("app.s4_ai_title")}
            </h2>
          </header>
          <p className="mt-1 text-xs text-text-secondary">
            {t("app.s4_ai_body_1")} <em>{t("app.s4_ai_body_2")}</em>{" "}
            {t("app.s4_ai_body_3")}
          </p>
        </section>
      </div>
    </StepLayout>
  );
}

function SectionCard({
  icon: Icon,
  title,
  count,
  countLabel,
  body,
  ctaLabel,
  ctaTo,
}: {
  icon: typeof GitBranch;
  title: string;
  count: number;
  countLabel: string;
  body: string;
  ctaLabel: string;
  ctaTo: string;
}) {
  return (
    <section className="rounded-xl border border-border-subtle bg-surface-default p-5">
      <header className="flex items-start gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-ai-bg text-ai">
          <Icon size={14} aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-text-primary">
              {title}
            </h2>
            <span className="rounded-full bg-surface-sunken px-2 py-0.5 text-xs font-medium text-text-secondary">
              {count} {countLabel}
            </span>
          </div>
          <p className="mt-1 text-xs text-text-secondary">{body}</p>
        </div>
        <Link
          to={ctaTo}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border-subtle bg-surface-default px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
        >
          {ctaLabel}
          <ChevronRight size={14} aria-hidden className="rtl:rotate-180" />
        </Link>
      </header>
    </section>
  );
}
